import { useEffect, useMemo, useRef, useState } from "react";
import { Icon, type IconName } from "./Icon";
import type { EmojiGroup } from "../lib/emojiSet.generated";
import { emojiMatches, pushRecent, readRecent } from "../lib/emojiPick";

/* EVERY EMOJI, BEHIND THE `+` (his 10-15 word, with an Instagram still: "on the right of the emojis you saw a plus. That shows
 * this when tapped. So we can choose more and on the bottom as you see is categories"). Instagram's sheet, our tokens: a
 * search field; "Your reactions" (the six) and "Recent" (this browser's last picks); every group with its heading; the
 * group tabs along the bottom, each scrolling to its group and lit while its group is in view. The 1,870 emoji are
 * `emojiSet.generated.ts` — the SAME list as `public.reaction_emoji`, so nothing offered here is refused — loaded only
 * when the `+` is pressed (its own chunk; the thread's bundle does not carry it). */

/** One LINE icon per tab, as in his still — never an emoji in the chrome (the suite's W81 ruler 9 caught the first cut). */
const TAB_ICON: Record<string, IconName> = {
  smileys: "smile", animals: "paw", food: "utensils", activities: "ball", travel: "car", objects: "bulb", symbols: "shapes", flags: "flag",
};

export function EmojiPicker({ six, chosen, onPick }: {
  six: readonly string[];
  chosen: string | null;
  onPick: (emoji: string) => void;
}) {
  const [groups, setGroups] = useState<readonly EmojiGroup[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState<string>("smileys");
  const [recent] = useState<string[]>(() => readRecent());
  const scroller = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let alive = true;
    import("../lib/emojiSet.generated")
      .then((m) => { if (alive) setGroups(m.EMOJI_GROUPS); })
      .catch(() => { if (alive) setFailed(true); });
    search.current?.focus({ preventScroll: true });
    return () => { alive = false; };
  }, []);

  const names = useMemo(() => new Map((groups ?? []).flatMap((g) => g.emojis.map(([e, n]) => [e, n] as const))), [groups]);
  const found = useMemo(() => (groups === null || query.trim() === "" ? null : emojiMatches(groups, query)), [groups, query]);

  function pick(e: string) {
    pushRecent(e);
    onPick(e);
  }

  // The lit tab follows the scroll: the last group whose heading has passed the scroller's top.
  function onScroll() {
    const s = scroller.current;
    if (s === null || groups === null) return;
    const top = s.getBoundingClientRect().top + 8;
    let current = groups[0]?.id ?? "smileys";
    for (const g of groups) {
      const h = s.querySelector<HTMLElement>(`[data-group="${g.id}"]`);
      if (h !== null && h.getBoundingClientRect().top <= top) current = g.id;
    }
    setActive(current);
  }

  function jump(id: string) {
    const s = scroller.current;
    const h = s?.querySelector<HTMLElement>(`[data-group="${id}"]`);
    if (s === null || s === undefined || h === null || h === undefined) return;
    // From the heading's REAL place (MEASURED: `offsetTop` under `content-visibility: auto` was an estimate and fell short).
    s.scrollTop += h.getBoundingClientRect().top - s.getBoundingClientRect().top;
    setActive(id);
  }

  const cell = (e: string) => (
    <button key={e} type="button" className={e === chosen ? "emoji-cell is-mine" : "emoji-cell"}
            aria-label={names.get(e) ?? e} aria-pressed={e === chosen} onClick={() => pick(e)}>{e}</button>
  );

  return (
    <div className="emoji-picker" role="dialog" aria-label="Choose a reaction">
      <label className="emoji-search">
        <span className="ht-visually-hidden">Search emoji</span>
        <input ref={search} type="search" placeholder="Search" value={query} autoComplete="off" spellCheck={false}
               onChange={(e) => setQuery(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter" && found !== null && found[0] !== undefined) { e.preventDefault(); pick(found[0][0]); } }} />
      </label>
      <div ref={scroller} className="emoji-scroll" onScroll={onScroll}>
        {failed ? <p className="emoji-note">The emoji could not be loaded. Close this and try again.</p>
          : groups === null ? <p className="emoji-note">Loading…</p>
          : found !== null ? (
            found.length === 0 ? <p className="emoji-note">No emoji for “{query.trim()}”.</p>
              : <div className="emoji-grid">{found.map(([e]) => cell(e))}</div>
          ) : (
            <>
              <h3 className="emoji-head">Your reactions</h3>
              <div className="emoji-grid">{six.map(cell)}</div>
              {recent.length > 0 && (
                <>
                  <h3 className="emoji-head">Recent</h3>
                  <div className="emoji-grid">{recent.map(cell)}</div>
                </>
              )}
              {groups.map((g) => (
                <section key={g.id} className="emoji-group">
                  <h3 className="emoji-head" data-group={g.id}>{g.name}</h3>
                  <div className="emoji-grid">{g.emojis.map(([e]) => cell(e))}</div>
                </section>
              ))}
            </>
          )}
      </div>
      {groups !== null && found === null && (
        <nav className="emoji-tabs" aria-label="Emoji groups">
          {groups.map((g) => (
            <button key={g.id} type="button" className={g.id === active ? "emoji-tab is-active" : "emoji-tab"}
                    aria-label={g.name} aria-current={g.id === active ? "true" : undefined} onClick={() => jump(g.id)}>
              <Icon name={TAB_ICON[g.id] ?? "smile"} size={18} />
            </button>
          ))}
        </nav>
      )}
    </div>
  );
}
